<template>
	<div class="dataset" @mouseleave="handleDataObjectAutoSave">
		<div v-if="!(activeDataObject && activeDataObject._id)" class="dataset__form">
			<FormConnectText />
		</div><!-- /.dataset__form -->

		<div v-else class="dataset__form">

			<p v-if="textReferences.length > 1" class="dataset-references">
				<Icon name="references" />
				
				<em>{{ multipleReferencesText }}</em>

				<Dropdown ref="textReferencesDropdown" placement="bottom-end">
					<template #header>
						<span class="dropdown__arrow" />
					</template>

					<DropdownNavDatasets
						:dataObjects="textReferences"
						@dropdownButtonClick="handleDropdownButtonClick"
					/>
				</Dropdown>
			</p><!-- /.datasets-references -->
			
			<Form className="form--dataset" :loading="isFormSubmitting">
				<FormHead>
					<input
						type="text"
						id="name"
						tabindex="0"
						:placeholder="NameInputPlaceholder"
						:value="formData.name"
						v-tooltip.top="formData.name"
						@input="handleNameInputChange"
					/>

					<p>{{ dataType }}</p>
				</FormHead>
				
				<FormBody>
					<component
						:is="FormFields"
						:formData="formData"
						:kind="activeDataObject.kind"
						:readOnly="readOnly"
					/>
				</FormBody>

				<div class="form__cta">
					<div class="form__cta-row">
						<div 
								v-if="userRoleWeight >= 10"
								class="form__cta-col form__cta-col--save"
							>
							<Button
								:title="activeDataObject.status"
								@onClick.prevent="handleDataObjectSave"
							>
								Complete This {{this.activeDataObjectType ? this.activeDataObjectType : 'dataset' }}
							</Button>

							<!--
								The form saves on its own (see handleDataObjectAutoSave),
								so the user has to be told what happened without having
								asked. Next to the button is where they are already
								looking when they expect a save.
							-->
							<p
								v-if="saveState.status !== 'idle'"
								class="form__save-state"
								:class="`form__save-state--${saveState.status}`"
							>
								{{ saveStateLabel }}

								<a
									v-if="saveState.status === 'error'"
									href="#"
									@click.prevent="retrySave"
								>Try again</a>
							</p>
						</div><!-- /.form__cta-col -->

						<div v-if="userRoleWeight < 100" class="form__cta-col">
							<Button
								className="tertiary"
								square
								:active="textPassagePopupVisibility()"
								:tooltip="tooltips.passage"
								@onClick.prevent="toggleTextPassagePopup"
							>
								<Icon name="documents" />
							</Button>

							<Button
								className="tertiary"
								v-if="userRoleWeight >= 10"
								v-tooltip.top="tooltips.unlink"
								@onClick.prevent="openUnlinkModal"
							>
								<Icon name="unlink" />

								Unlink
							</Button>
							
							<Button
								className="tertiary"
								v-if="userRoleWeight >= 10"
								v-tooltip.top="tooltips.deleteText"
								@onClick.prevent="openDeleteModal"
							>
								<Icon name="trash" />

								Delete
							</Button>
						</div><!-- /.form-__cta-col -->

						<div v-if="userRoleWeight >= 100" class="form__cta-col" style="width: 100%;">
							<Button
								className="tertiary"
								square
								:active="sciscorePopupVisibility()"
								:tooltip="tooltips.sciscore"
								@onClick.prevent="toggleSciscoreLogsPopup"
								:disabled="activeDataObject.kind === 'dataset' || activeDataObject.kind === 'protocol'"
							>
								<Icon name="sciscore" />
							</Button>

							<Button
								className="tertiary"
								square
								:active="this.formData.flagged"
								:tooltip="tooltips.flag"
								@onClick.prevent="toggleIssues"
							>
								<Icon name="flag" />
							</Button>

							<Button
								className="tertiary"
								square
								:active="textPassagePopupVisibility()"
								:tooltip="tooltips.passage"
								@onClick.prevent="toggleTextPassagePopup"
							>
								<Icon name="documents" />
							</Button>

							<Button
								square
								className="tertiary"
								modifier="is-warning"
								:tooltip="tooltips.unlink"
								@onClick.prevent="openUnlinkModal"
							>
								<Icon name="unlink" />
							</Button>

							<Button
								className="tertiary"
								square
								modifier="is-warning"
								:tooltip="tooltips.duplicateText"
								@onClick.prevent="handleDuplicateDataObject"
							>
								<Icon name="document_new" />
							</Button>

							<Button
								className="tertiary"
								square
								modifier="is-warning"
								:tooltip="tooltips.deleteText"
								@onClick.prevent="openDeleteModal"
							>
								<Icon name="trash" />
							</Button>

							<Button
								style="float: right;"
								className="tertiary"
								square
								:active="dataObjectLogsPopupVisibility()"
								:tooltip="tooltips.logs"
								@onClick.prevent="toggleDataObjectsLogsPopup"
							>
								<Icon name="document_view" />
							</Button>
						</div><!-- /.form-__cta-col -->
					</div> <!-- /.form__cta-row -->
				</div> <!-- /.form__cta -->
			</Form>

			<PopupDraggable ref="SciscorePopup" name="sciscore-popup" size="small">
				<Grid rowGap="medium">
					<GridColumn>
						<Field
							name="suggestedEntity"
							v-model.trim="formData.suggestedEntity"
						>
							<Icon name="windows" />

							Suggested Name of Entity
						</Field>
					</GridColumn>

					<GridColumn v-if="activeDataObject.kind !== 'reagent'">
						<Field
							name="suggestedURL"
							v-model.trim="formData.suggestedURL"
						>
							<Icon name="chain" />

							Suggested URL
						</Field>
					</GridColumn>

					<GridColumn>
						<Field
							name="suggestedRRID"
							v-model.trim="formData.suggestedRRID"
						>
							<Icon name="key" />

							Suggested RRID
						</Field>
					</GridColumn>

					<GridColumn>
						<div v-if="isAutoFillingSugdestedFields" class="list">
							<i>Automtically fill suggested fields...</i>
						</div>
						<ListOfRRIDs
							v-if="!isAutoFillingSugdestedFields"
							name="searchRRIDs"
							:entity="formData.searchRRIDs.entity"
							:error="formData.searchRRIDs.err"
							:results="formData.searchRRIDs.results"
							@clickOnRRID="handleCickOnRRID"
						>
							<div class="list">
								Suggested RRIDs from the<a href="https://scicrunch.org/" target="_blank"> Scicrunch API </a>
							</div>
						</ListOfRRIDs>
					</GridColumn>

					<GridColumn>
							<Button
								@onClick.prevent="$refs.SciscorePopup.hideModal()"
							>
								Done
							</Button>
					</GridColumn>	
				</Grid>
			</PopupDraggable>

			<PopupDraggable ref="TextPassagePopup" name="text-passage-popup" size="small">
				<RichtextEntry label="Connected Text Passages" icon="documents">
					<p v-for="sentence in activeDataObject.sentences" :key="sentence.id">
						{{sentence.text}}
					</p>
				</RichtextEntry>
			</PopupDraggable>

			<PopupDraggable ref="DataObjectLogsPopup" name="logs-popup" size="small" class="DataObjectLogsPopup">
				<div class="popup-container">
					<h4>DataObject Logs</h4>
					<div v-if="activeDataObjectLogs.loading">
						<i>Loading DataObject Logs...</i>
					</div>
					<div v-if="activeDataObjectLogs.err">
						{{ activeDataObjectLogs.err }}
					</div>
					<div v-if="!activeDataObjectLogs.loading && activeDataObjectLogs.list.length <= 0">
						This object has just been created and does not yet have any logs available
					</div>

					<DataObjectLogs
							:loading="activeDataObjectLogs.loading"
							:logs="activeDataObjectLogs.list"
							@clickOnRestoreDataObject="handleRestoreDataObject"
						>
					</DataObjectLogs>
				</div>
			</PopupDraggable>
		</div><!-- /.dataset__form -->
	</div><!-- /.dataset -->
</template>

<script>
/**
 * External Dependencies
 */
import { reactive } from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { updatedDiff } from 'deep-object-diff'
import async from 'async';

/**
 * Internal Dependencies
 */
import variables from '@/utils/scss-variables'

import PopupDraggable from '@/components/popup/popup-draggable';
import PopupConfirm from '@/components/popup/popup-confirm';
import RichtextEntry from '@/components/richtext-entry/richtext-entry';
import Icon from '@/components/icon/icon'
import Button from '@/components/button/button'
import Field from '@/components/field/field';
import ListOfRRIDs from '@/components/list-rrids/list-rrids';
import DataObjectLogs from '@/components/dataobject-logs/dataobject-logs';
import Dropdown, { DropdownNavDatasets } from '@/components/dropdown/dropdown'
import Grid, { GridColumn } from '@/components/grid/grid';
import Form, { FormBody, FormHead } from '@/components/form/form';
import FormConnectText from '@/blocks/form-connect-text/form-connect-text';
import FormDataset from '@/blocks/form-dataset/form-dataset-default';
import FormDatasetCode from '@/blocks/form-dataset/form-dataset-code';
import FormDatasetMaterial from '@/blocks/form-dataset/form-dataset-material';
import FormDatasetProtocols from '@/blocks/form-dataset/form-dataset-protocols';
import { clearDropdown } from '@/utils/use-dropdowns';

import API from '@/lib/datasets/api';
import URLMANAGER from '@/lib/datasets/url';

// How long to wait before each automatic retry of a failed save. One entry
// per retry, so a save is attempted RETRY_DELAYS.length + 1 times in all
// before the user is asked.
const RETRY_DELAYS = [1000, 3000, 7000];

export default {
	/**
	 * Name
	 */
	name: "Dataset",

	/**
	 * Components
	 */
	components: {
		Grid,
		GridColumn,
		Field,
		variables,
		Icon,
		Form,
		PopupDraggable,
		PopupConfirm,
		Button,
		Dropdown,
		DropdownNavDatasets,
		FormHead,
		FormBody,
		RichtextEntry,
		FormConnectText,
		ListOfRRIDs,
		DataObjectLogs
	},

	/**
	 * Props
	 */
	props: {
		dataObject: {
			type: Object,
			default: () => {}
		},
		readOnly: {
			type: Boolean,
			default: false
		}
	},

	/**
	 * Data
	 */
	data() {
		return {
			// What the save system is doing, shown above the form actions.
			// status: idle | saving | retrying | saved | error | conflict
			saveState: {
				status: 'idle',
				at: null,
				attempt: 0
			},
			// Payloads waiting to be written, one per data object. Keeping
			// them here rather than in the form is what lets the user leave a
			// data object - or the page - without losing the write.
			saveQueue: [],
			retryTimer: null,
			formData: {
				err: "Not available",
				searchRRIDs: {
					err: "",
					results: [],
					entity: {
						name: "",
						URL: ""
					}
				},
			},
			originalFormData: {},
			formDataIssues: {
				active: [''],
				author: '',
				createdAt: '',
				comment: '',
			},
			activeDataObjectLogs: {
				list: [],
				loading: false,
				error: "Service unavailable"
			},
			isAutoFillingSugdestedFields: false,
			isFormSubmitting: false,
			multipleReferencesText: 'Multiple references share this text selection',
			NameInputPlaceholder: 'Enter...',
			tooltips: {
				logs: 'Show logs of this object',
				sciscore: 'Suggest citation information for this object',
				flag: 'Flag Issues For Author',
				passage: 'Show Text Passage',
				unlink: 'Unlink selected sentence to this item',
				deleteText: 'Delete this item',
				duplicateText: 'Duplicate this item'
			},
			modals: {
				confirmDelete: {
					message: "Do you wish to permanently delete this item?",
					confirm: "Yes, Delete",
					cancel: "No, Keep It",
				},
				confirmUnlink: {
					message: "Do you wish to unlink the selected text passage from this item?",
					confirm: "Yes, Unlink",
					cancel: "No, Keep It",
				}
			},
			issuesList: [
				'URL broken',
				'Input incorrect (wrong cat#/RRID/PID/DOI/other)',
				'Item not yet publicly accessible',
				'Not an appropriate reference',
				'Dataset not provided',
				'Other'
			]
		}
	},

	/**
	 * Watch
	 */
	watch: {
		activeDataObjectId: {
			handler(newId, oldId) {
				// Leaving a data object is one of the moments the form saves by
				// itself: the edits must not be lost because the user clicked
				// the next item in the list. The form still holds the previous
				// object here, which is exactly what has to be written.
				if (oldId && oldId !== newId) this.handleDataObjectAutoSave();

				if (!this.activeDataObjectId) {
					this.$refs.TextPassagePopup?.hideModal();
					this.$refs.DataObjectLogsPopup?.hideModal();
					this.$refs.SciscorePopup?.hideModal();
					return;
				}
				if (this.$refs.SciscorePopup?.isVisible) this.handleSciscoreClick();
				if (this.$refs.DataObjectLogsPopup?.isVisible) this.handleDataObjectLogsClick();
				this.populateFormData();
			},
			deep: true
		}
	},

	/**
	 * Mounted
	 */
	mounted() {
		// Closing the tab or hitting reload is the one exit the app cannot
		// save through - the write would be cancelled with the page - so the
		// browser is asked to confirm instead.
		window.addEventListener('beforeunload', this.warnIfUnsaved);
	},

	/**
	 * Before unmount
	 */
	beforeUnmount() {
		window.removeEventListener('beforeunload', this.warnIfUnsaved);
		this.clearRetry();
		// Leaving the page: flush whatever the form still holds.
		this.handleDataObjectAutoSave();
	},

	/**
	 * Computed
	 */
	computed: {
		...mapGetters('pdfViewer', [
			'documentHandler',
			'dataTypes',
			'activeDataObject',
			'activeDataObjectId',
			'activeDataObjectType',
			'activeSentence',
			'dataObjects'
		]),
		...mapGetters('account', ['userRoleWeight', 'user']),
		FormFields() {
			if (this.activeDataObjectType === 'code') return FormDatasetCode;
			if (this.activeDataObjectType === 'material') return FormDatasetMaterial;
			if (this.activeDataObjectType === 'protocol') return FormDatasetProtocols;

			return FormDataset;
		},
		dataType() {
			return this.dataObject?.dataType ? this.dataObject.dataType : 'Undefined Type'
		},
		textReferences() {
			return this.dataObjects.filter((dataObject) => dataObject.sentences.some((sentence) => sentence.id === this.activeSentence?.id));
		},
		cssVariables() {
			return variables
		},
		saveStateLabel() {
			const { status, at, attempt } = this.saveState;
			// Kept short: this sits beside the button, not on a line of its own.
			if (status === 'saving') return 'Saving…';
			if (status === 'retrying') return `Retrying (${attempt}/${RETRY_DELAYS.length})…`;
			if (status === 'saved') return `Saved at ${at.toLocaleTimeString()}`;
			if (status === 'error') return 'Not saved.';
			if (status === 'conflict') return 'Changed elsewhere - not saved.';
			return '';
		}
	},

	/**
	 * Methods
	 */
	methods: {
		...mapActions('pdfViewer', [
			'deleteDataObject',
			'unlinkSentenceFromDataObject',
			'setActiveDataObject',
			'setActiveDataObjectType',
		]),
		textPassagePopupVisibility() {
			return !!this?.$refs?.TextPassagePopup?.isVisible;
		},
		sciscorePopupVisibility() {
			return !!this?.$refs?.SciscorePopup?.isVisible;
		},
		dataObjectLogsPopupVisibility() {
			return !!this?.$refs?.DataObjectLogsPopup?.isVisible;
		},
		populateFormData() {
			// The merge keeps whatever the form held before, so a field the
			// incoming data object lacks survives from the one edited last.
			// `updatedAt` must not: it is the version token the API checks on
			// save, and one carried over from another data object (a freshly
			// created one arrives without its own) makes the first save of the
			// new object refused as a conflict it never had.
			const updatedAt = this.activeDataObject?.updatedAt;
			this.formData = { ...this.formData, ...this.activeDataObject, updatedAt };
			this.originalFormData = { ...this.originalFormData, ...this.activeDataObject, updatedAt };
			// Only the display resets here. A save still in flight belongs to
			// the data object being left, and must be left alone to finish.
			if (this.saveState.status === 'idle' || this.saveState.status === 'saved') {
				this.saveState = { status: 'idle', at: null, attempt: 0 };
			}
		},
		handleNameInputChange(e) {
			this.formData = { ...this.formData, name: e.target.value }
			if (this.$refs.SciscorePopup?.isVisible) this.toggleSciscoreLogsPopup();
		},
		handleDropdownButtonClick(dataObject) {
			if (dataObject._id === this.activeDataObjectId) return

			// Close text references dropdown when the active dataObject changes
			clearDropdown(this.$refs.textReferencesDropdown.$el);
			
			this.setActiveDataObject({
				dataObject,
				scrollToSentence: true
			});
		},
		toggleTextPassagePopup() {
			if (this.$refs.TextPassagePopup.isVisible) this.$refs.TextPassagePopup.hideModal(); 
			else this.$refs.TextPassagePopup.showModal();
		},
		toggleDataObjectsLogsPopup() {
			if (this.$refs.DataObjectLogsPopup.isVisible) this.$refs.DataObjectLogsPopup.hideModal(); 
			else this.$refs.DataObjectLogsPopup.showModal();
			return this.handleDataObjectLogsClick();
		},
		toggleSciscoreLogsPopup() {
			if (this.$refs.SciscorePopup.isVisible) this.$refs.SciscorePopup.hideModal(); 
			else this.$refs.SciscorePopup.showModal();
			return this.handleSciscoreClick();
		},
		handleDataObjectLogsClick() {
			if (!this.$refs.DataObjectLogsPopup.isVisible) return;
			return API.dataObjects.getLogs({
				dataObject: this.activeDataObject,
			}, (err, query) => {
				if (err) {
					this.activeDataObjectLogs.error = err.toString();
					return;
				}
				if (query.err) {
					this.activeDataObjectLogs.error = query.err.toString();
					return;
				}
				this.activeDataObjectLogs.error = "";
				this.activeDataObjectLogs.list = [];
				let create = query.res.filter((item) => {
					return item.kind.key === "create";
				}).sort(function (a, b) {
					return new Date(a.date) - new Date(b.date);
				});
				let updates = query.res.filter((item) => {
					return item.kind.key === "update";
				}).sort(function (a, b) {
					return new Date(a.date) - new Date(b.date);
				});
				this.activeDataObjectLogs.list.push({
						_id: create[0]._id,
						account: create[0].account,
						dataObject: {
							old: create[0].state,
							new: create[0].state,
						},
						date : create[0].date
					});
				if (updates.length > 0) {
					for (let i = 0; i < updates.length; i++) {
						this.activeDataObjectLogs.list.push({
							_id: updates[i]._id,
							account: updates[i].account,
							dataObject: {
								old: updates[i].state,
								new: i + 1 < updates.length ? updates[i + 1].state : this.activeDataObject,
							},
							date : updates[i].date
						});
					}
				}
			});
		},
		handleSciscoreClick() {
			if (!this.$refs.SciscorePopup.isVisible) return;
			const documentHandler = this.documentHandler;
			const entity = { name: this.formData.name.toString(), URL: "" };
			this.formData.searchRRIDs = { err: "", results: [], entity: entity };
			// Reactive on purpose: the callbacks below write into these category objects after they have been
			// pushed into formData.searchRRIDs.results, and Vue 3 only tracks writes made through its proxy
			let results = reactive({});
			if (this.activeDataObject.kind !== "code" && this.activeDataObject.kind !== "software" && this.activeDataObject.kind !== "reagent") {
				this.formData.searchRRIDs.err = `Suggested RRIDs service is not available for DATASETS & PROTOCOLS`;
				return;
			}
			if (entity.name && entity.name.length > 0) {
				// Build results
				let href = this.activeDataObject.kind === "reagent" ? API.reagents.url : API.software.url;
				results.fromCustomList = { index: 0, text: "Custom List", href: URLMANAGER.buildURL(`${href}/customList`, {}, { origin: true }), entities: [], error: "Requesting service...", requestAPI: false };
				results.resources = { index: 1, text: "Resources", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.antibody = { index: 1, text: "Antibodies", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.tool = { index: 1, text: "Tools", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.plasmid = { index: 1, text: "Plasmid", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.organism = { index: 1, text: "Organisms", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.cellLine = { index: 1, text: "Cell Lines", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.biosamples = { index: 1, text: "Bio Samples", href: "", entities: [], error: "Requesting service...", requestAPI: false };
				results.fromAPI = { index: 2, text: "Scicrunch API", href: "", entities: [], error: "Requesting service...", requestAPI: true };
				// Set results depending active dataObject kind
				this.formData.searchRRIDs.results.push(results.fromAPI);
				if (this.activeDataObject.kind === `software` || this.activeDataObject.kind === `code`) {
					this.formData.searchRRIDs.results.push(results.fromCustomList);
					this.formData.searchRRIDs.results.push(results.tool);
				} else if (this.activeDataObject.kind === `reagent`) {
					this.formData.searchRRIDs.results.push(results.fromCustomList);
					this.formData.searchRRIDs.results.push(results.antibody);
					this.formData.searchRRIDs.results.push(results.plasmid);
					this.formData.searchRRIDs.results.push(results.organism);
					this.formData.searchRRIDs.results.push(results.cellLine);
				}
				return async.map([
					(next) => {
						if (this.activeDataObject.kind !== "code" && this.activeDataObject.kind !== "software") return next();
						return API.software.find({ name: entity.name }, (_, query) => {
							console.log(_, query);
							let error = "";
							let entities = [];
							if (_) error = "Custom software list is currently not available";
							if (!query?.err && Array.isArray(query?.res)) entities = query.res;
							results.fromCustomList.error = error;
							results.fromCustomList.entities = entities;
							return next();
						});
					},
					(next) => {
						if (this.activeDataObject.kind !== "reagent") return next();
						let source = this.formData.source.toString();
						let catalogNumber = this.formData.catalogNumber.toString();
						let RRID = this.formData.RRID.toString();
						let reuse = this.formData.reuse;
						let subType = this.formData.subType.toString();
						let name = this.formData.name.toString();
						return API.reagents.find({ source, subType, reuse, RRID, catalogNumber, name }, (_, query) => {
							console.log(_, query);
							let error = "";
							let entities = [];
							if (_) error = "Custom software list is currently not available";
							if (!query?.err && Array.isArray(query?.res)) entities = query.res;
							results.fromCustomList.error = error;
							results.fromCustomList.entities = entities;
							return next();
						});
					},
					(next) => {
						return documentHandler.getRRIDsFromSciscore(entity.name, (_, query) => {
							console.log(_, query);
							let error = "";
							if (_) error = "Suggested RRIDs service is currently not available";
							if (query?.err) error = "Suggested RRIDs service has returned an error";
							results.fromAPI.error = error;
							// Build "links of API results" depending of the data object kind
							results.fromAPI.href = query?.res?.entity?.URLs?.API;
							results.resources.href = query?.res?.entity?.URLs?.resources;
							results.antibody.href = query?.res?.entity?.URLs?.antibody;
							results.tool.href = query?.res?.entity?.URLs?.tool;
							results.plasmid.href = query?.res?.entity?.URLs?.plasmid;
							results.organism.href = query?.res?.entity?.URLs?.organism;
							results.cellLine.href = query?.res?.entity?.URLs?.cellLine;
							results.biosamples.href = query?.res?.entity?.URLs?.biosamples;
							// Populate API results
							for (let key in query?.res?.RRIDs?.values) {
								results.fromAPI.entities.push({ href: query?.res?.RRIDs?.URLs?.resources[key], suggestedEntity: query?.res?.RRIDs?.values[key].Resource_Name, RRID: query?.res?.RRIDs?.values[key].scr_id })
							}
							return next();
						});
					},
					(next) => {
						let search = `${entity.name} ${this.formData.RRID}`.trim();
						return API.scicrunch.searchEntity({ entity: search, kind: this.activeDataObject.kind }, (_, query) => {
							console.log(_, query);
							let error = "";
							if (_) error = "Suggested RRIDs service is currently not available";
							if (query?.err) error = "Suggested RRIDs service has returned an error";
							results.resources.error = error;
							results.antibody.error = error;
							results.tool.error = error;
							results.plasmid.error = error;
							results.organism.error = error;
							results.cellLine.error = error;
							results.biosamples.error = error;

							results.resources.entities = query?.res?.resources;
							results.antibody.entities = query?.res?.antibody;
							results.tool.entities = query?.res?.tool;
							results.plasmid.entities = query?.res?.plasmid;
							results.organism.entities = query?.res?.organism;
							results.cellLine.entities = query?.res?.cellLine;
							results.biosamples.entities = query?.res?.biosamples;
							return next();
						});
					}
				], (fn, next) => {
					return fn(function() {
						return next();
					});
				}, () => {
				});
			} else {
				this.formData.searchRRIDs.results = [];
				this.formData.searchRRIDs.err = "You must provide a name to get suggested RRIDs";
			}
		},
		/** True when the form holds edits the server has not accepted yet. */
		hasUnsavedChanges() {
			if (this.saveQueue.length) return true;
			if (this.saveState.status === 'error' || this.saveState.status === 'conflict') return true;
			return Object.keys(updatedDiff({ ...this.originalFormData }, { ...this.formData })).length > 0;
		},

		warnIfUnsaved(event) {
			if (!this.hasUnsavedChanges()) return;
			event.preventDefault();
			// Chrome ignores the text and shows its own, but it needs a value.
			event.returnValue = '';
			return '';
		},

		handleDataObjectAutoSave() {
			let formData = { ...this.formData };
			let originalFormData = { ...this.originalFormData };
			let updates = Object.keys(updatedDiff(originalFormData, formData));
			let needSave = updates.length > 0;
			if (!needSave) return;
			// A failure is already on screen with a "Try again" of its own.
			// Keep the queued payload current, but let the user decide when to
			// attempt it again rather than retrying every time the mouse moves.
			const waitingOnTheUser = this.saveState.status === 'error' || this.saveState.status === 'conflict';
			return this.enqueueSave(this.buildSavePayload(), { start: !waitingOnTheUser });
		},
		/**
		 * Collect what should be written for the data object on screen.
		 * Split out of handleDataObjectSave so a retry can rebuild nothing and
		 * simply resend the payload that failed.
		 */
		buildSavePayload() {
			let formData = { ...this.formData };

			// Set issues property when user is curator
			if (this.userRoleWeight >= 100) {
				if (formData.flagged) {
					if (!formData.issues) {
						formData.issues = {};
						formData.issues.active = []
						formData.issues.author = this.user.username
						formData.issues.createdAt = new Date()
					} else {
						if (!Array.isArray(formData.issues.active)) formData.issues.active = []
						if (!formData.issues.author) formData.issues.author = this.user.username
						if (!(formData.issues.createdAt instanceof Date)) formData.issues.createdAt = new Date()
					}
					// No longer use these fields, clean code ASAP
					formData.issues.active = undefined;
					formData.issues.comment = undefined;
				} else {
					formData.issues = null
				}
			}

			return formData;
		},

		/**
		 * Save the data object on screen.
		 *
		 * The form saves by itself - on mouseleave, when the user picks another
		 * data object, when the page is left - so a failed write must not be
		 * quiet, and must not depend on the user still looking at that object.
		 * Saves therefore go through a queue, one payload per data object:
		 *
		 *  - a write that fails is retried on its own, with a growing delay,
		 *    which covers what this is actually for - a server restart, a few
		 *    seconds of lost connectivity - and only then asks the user, with a
		 *    button that resends the very payload that failed;
		 *  - editing the same object again while its save is in flight replaces
		 *    the queued payload rather than starting a second write;
		 *  - the user can move on to another data object, and the one they left
		 *    still gets written.
		 */
		handleDataObjectSave() {
			if (this.userRoleWeight < 10) return;
			return this.enqueueSave(this.buildSavePayload());
		},

		/**
		 * Queue a payload, one per data object, newest wins.
		 * `start` is false when the queue should take the newer edits but not
		 * begin another attempt - the form is already showing the user a
		 * failure, and it is theirs to act on.
		 */
		enqueueSave(payload, { start = true } = {}) {
			if (!payload || !payload._id) return;
			const index = this.saveQueue.findIndex((item) => item._id === payload._id);
			if (index >= 0) this.saveQueue.splice(index, 1, payload);
			else this.saveQueue.push(payload);
			if (!start) return;
			if (this.saveState.status === 'saving' || this.saveState.status === 'retrying') return;
			return this.processSaveQueue();
		},

		/** Send the head of the queue. */
		processSaveQueue() {
			const payload = this.saveQueue[0];
			if (!payload) return;
			this.clearRetry();
			this.saveState.attempt = 0;
			return this.sendQueuedSave();
		},

		/** Send the head of the queue, retrying by itself while it makes sense. */
		sendQueuedSave() {
			const payload = this.saveQueue[0];
			if (!payload) return;
			const documentHandler = this.documentHandler;
			this.isFormSubmitting = true;
			this.saveState.status = 'saving';

			return documentHandler.saveDataObject(payload, (err, res) => {
				if (err || !res) {
					// 409: the data object was changed by someone else and the
					// API refused the write. Retrying can only fail again, and
					// forcing it would be the overwrite this is here to stop.
					if (err && err.status === 409) {
						this.saveQueue.shift();
						this.isFormSubmitting = false;
						this.saveState = { status: 'conflict', at: null, attempt: 0 };
						this.openConfirmModal({
							title: 'Not saved',
							message: `Your local version is no longer up to date - someone else changed this data object while you were editing it, so the app did not save your changes over theirs. Nothing was written. Reload to see their version; your own edits stay on screen until you do.`,
							confirm: 'Reload this data object',
							onConfirm: this.reloadActiveDataObject,
							cancel: 'Keep editing'
						});
						return;
					}
					if (this.saveState.attempt >= RETRY_DELAYS.length) {
						// Out of attempts: hand it to the user. The payload
						// stays at the head of the queue so "Try again" resends
						// exactly this, even if they have moved on since.
						this.isFormSubmitting = false;
						this.saveState.status = 'error';
						this.openConfirmModal({
							title: 'Not saved',
							message: `Your changes to "${payload.name || 'this data object'}" could not be saved after ${RETRY_DELAYS.length + 1} attempts - the server refused the request, so nothing was recorded.`,
							confirm: 'Try again',
							onConfirm: this.retrySave,
							cancel: 'Later'
						});
						return;
					}
					const delay = RETRY_DELAYS[this.saveState.attempt];
					this.saveState.attempt += 1;
					this.saveState.status = 'retrying';
					this.retryTimer = setTimeout(this.sendQueuedSave, delay);
					return;
				}

				const newDataObject = res;
				this.saveQueue.shift();
				this.saveState = { status: 'saved', at: new Date(), attempt: 0 };
				this.isFormSubmitting = false;

				// This may have been the flush of a data object the user has
				// already left. The form has moved on, and refreshing it or
				// re-selecting the saved object would drag them back.
				if (this.formData._id === payload._id) {
					// Take the version the API just handed back. Without it the
					// form would still hold the one it loaded, and its own next
					// save would be refused as a conflict with itself. Only this
					// field is copied over: anything else would overwrite what
					// the user typed while the save was in flight.
					if (newDataObject.updatedAt) this.formData.updatedAt = newDataObject.updatedAt;
					this.originalFormData = { ...this.formData }

					if (this.activeDataObjectType !== newDataObject.dataObjectType) {
						this.setActiveDataObjectType(newDataObject.dataObjectType);
					}

					this.setActiveDataObject({
						dataObject: newDataObject,
						scrollToSentence: false
					});
				}

				// Anything else waiting goes now.
				if (this.saveQueue.length) return this.processSaveQueue();
			})
		},

		/** Resend the failed payload, from the modal or the status line. */
		retrySave() {
			if (!this.saveQueue.length) return;
			this.clearRetry();
			this.saveState.attempt = 0;
			return this.sendQueuedSave();
		},

		/**
		 * Drop the local edits and show what the server currently holds.
		 * The data objects are fetched once with the document and kept in the
		 * store, and nothing re-reads a single one from the server, so the
		 * honest way to show their version is to load the page again - with
		 * the same data object selected.
		 */
		reloadActiveDataObject() {
			const dataObjectId = this.formData._id;
			const url = new URL(location.href);
			if (dataObjectId) {
				url.searchParams.delete('selectedDataObjectId');
				url.searchParams.append('selectedDataObjectId', dataObjectId);
			}
			location.href = url.toString();
			location.reload();
		},

		clearRetry() {
			if (this.retryTimer) {
				clearTimeout(this.retryTimer);
				this.retryTimer = null;
			}
		},
		toggleIssues() {
			this.formData.flagged = !this.formData.flagged;
			this.handleDataObjectSave();
		},
		setIssuesFormVisibility(value) {
			this.formData.flagged = value;
		},
		handleDuplicateDataObject() {
			const documentHandler = this.documentHandler;
			this.isFormSubmitting = true;
			let formData = { ...this.formData };

			documentHandler.duplicateDataObject(formData, (err, res) => {
				// A failed write used to be swallowed here: the callback took
				// the error as `_`, then read `res.dataObjectType` of an
				// undefined result. The form stayed locked, nothing was said,
				// and the user's edits were gone on the next reload. Tell them
				// instead, and give the form back so they can retry.
				if (err || !res) {
					this.isFormSubmitting = false;
					this.openConfirmModal({
						title: 'Not duplicated',
						message: `This data object could not be duplicated - the server refused the request, so nothing was created. Try again, and check your connection if it keeps failing.`,
						confirm: 'OK',
						cancel: null
					});
					return;
				}

				const newDataObject = res;
				// `this.res` here was undefined, so the form kept the source
				// data object (and its version) instead of the copy.
				this.formData = { ...this.formData, ...newDataObject }
				this.originalFormData = { ...this.formData, ...newDataObject }
				
				if (this.activeDataObjectType !== newDataObject.dataObjectType) {
					this.setActiveDataObjectType(newDataObject.dataObjectType);
				}

				this.setActiveDataObject({
					dataObject: newDataObject,
					scrollToSentence: true
				});

				this.isFormSubmitting = false;
			})
		},
		openDeleteModal() {
			this.openConfirmModal({
				...this.modals.confirmDelete,
				onConfirm: this.deleteDataObject,
			})
		},
		openUnlinkModal() {
			this.openConfirmModal({
				...this.modals.confirmUnlink,
				onConfirm: this.unlinkSentenceFromDataObject,
			})
		},
		handleRestoreDataObject(dataObject) {
			return API.dataObjects.update({
				documentId: dataObject.document,
				dataObjects: [dataObject],
			}, (err, query) => {
				if (err) {
					this.activeDataObjectLogs.error = err.toString();
					return;
				}
				if (query.err) {
					this.activeDataObjectLogs.error = query.err.toString();
					return;
				}
				// add params selectedDataObjectId with dataObject id
				let url = new URL(location.href);
				let params = Object.assign({}, URLMANAGER.getParamsOfCurrentURL(), this.$route.query);
				if (url.searchParams.get('selectedDataObjectId') !== null) url.searchParams.delete('selectedDataObjectId');
				url.searchParams.append('selectedDataObjectId', dataObject._id);
				if (params.token) {
					if (url.searchParams.get('selectedDataObjectId') !== null) url.searchParams.delete('token');
					url.searchParams.append('token', params.token);
				}
				window.location.href = url.toString();
				// return window.location.reload();
			});
		},
		handleCickOnRRID(opts) {
			let data = opts.data;
			let requestAPI = opts.requestAPI;
			this.isAutoFillingSugdestedFields = true;
			// case Sciscrunch API must be required
			if (requestAPI) {
				return API.scicrunch.processRRID({ RRID: data.RRID }, (err, res) => {
					if (err) return alert("Service not available at the moment");
					if (res.err) return alert(`RRID ${data.RRID} not found`);
					this.formData.suggestedEntity = res.res.name;
					this.formData.suggestedURL = res.res.URL;
					this.formData.suggestedRRID = `${res.res.name} (RRID:${res.res.RRID})`;
					this.isAutoFillingSugdestedFields = false;
				});
			} else {
				// case data come from custom list of software
				this.formData.suggestedEntity = data.suggestedEntity;
				this.formData.suggestedURL = data.suggestedURL;
				this.formData.suggestedRRID = data.suggestedRRID;
				this.isAutoFillingSugdestedFields = false;
			}
		}
	},

	/**
	 * Created
	 */
	created () {
		this.populateFormData();
	},
}
</script>
